import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Typography, Button, Box, CircularProgress } from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import { getQuizByCategory } from "../../../services/database/database_service";
import LoadingIndicator from "../components/LoadingIndicator";
import { useLanguage } from "../../../services/localization/LanguageContex";

function QuizSubScreenOne() {
  const { t, language } = useLanguage();
  const { category } = useParams();
  const [quizList, setQuizList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [answerChecked, setAnswerChecked] = useState(false);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);

  const [audio, setAudio] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    async function fetchQuizByCategory() {
      try {
        const data = await getQuizByCategory(category);
        setQuizList(data.documents);
      } catch (err) {
        setError("Could not fetch quiz. Please try again later.");
      } finally {
        setLoading(false);
      }
    }
    fetchQuizByCategory();
  }, [category]);

  useEffect(() => {
    if (!loading && quizList.length > 0) {
      generateOptions();
      setImageLoading(true); // Reset image loading on question change
    }
  }, [loading, currentQuestion]);

  useEffect(() => {
    const stopAudio = () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
    return () => stopAudio();
  }, [audio]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && audio) {
        audio.pause();
      }
    };

    const handleBeforeUnload = () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [audio]);

  const generateOptions = () => {
    if (!quizList.length) return;

    const correctItem = quizList[currentQuestion];
    const optionsSet = new Map();
    optionsSet.set(correctItem.answer, correctItem);

    while (optionsSet.size < Math.min(4, quizList.length)) {
      const randIndex = Math.floor(Math.random() * quizList.length);
      const item = quizList[randIndex];
      optionsSet.set(item.answer, item);
    }

    const shuffled = Array.from(optionsSet.values()).sort(
      () => Math.random() - 0.5
    );
    setOptions(shuffled);
  };

  const playAudio = (url) => {
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }

    const newAudio = new Audio(url);
    newAudio.play().catch((err) => {
      console.error("Playback failed:", err);
    });

    setAudio(newAudio);
  };

  const handleOptionClick = (option) => {
    if (answerChecked) return;
    setSelectedOption(option);

    if (option?.audioUrl) {
      playAudio(option.audioUrl);
    }
  };

  const handleCheckAnswer = () => {
    setAnswerChecked(true);
    const correct = quizList[currentQuestion].answer;
    if (selectedOption.answer === correct) {
      setScore((prev) => prev + 1);
    }
  };

  const handleNextQuestion = () => {
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    if (currentQuestion + 1 < quizList.length) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedOption(null);
      setAnswerChecked(false);
    } else {
      setShowResult(true);
    }
  };

  const restartQuiz = () => {
    window.location.reload();
  };

  if (loading) {
    return (
      <Box sx={{ p: 6, pt: 2, textAlign: "center" }}>
        <LoadingIndicator />
      </Box>
    );
  }

  if (showResult) {
    const percentage = Math.round((score / quizList.length) * 100);

    return (
      <Box
        sx={{
          // p: 4, pt: 6,
          px: { xs: 1, sm: 6, md: 6 },
          pt: 2,
          pb: 4,

          textAlign: "center",
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ mb: 4 }}>
          {score === quizList.length && language === "en" ? "Perfect! " : ""}
          {t("Your score")}
        </Typography>

        <Box sx={{ position: "relative", display: "inline-flex", mb: 4 }}>
          <CircularProgress
            variant="determinate"
            value={percentage}
            size={160}
            thickness={4}
            sx={{ color: "#4caf50", backgroundColor: "transparent" }}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: "absolute",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="h5"
              component="div"
              color="textPrimary"
              fontWeight="medium"
            >
              {`${percentage} %`}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Button
            onClick={restartQuiz}
            variant="contained"
            endIcon={<ReplayIcon />}
          >
            {t("Try again")}
          </Button>
          <Button
            onClick={() => (window.location.href = "/home/quiz")}
            variant="contained"
            endIcon={<ExitToAppIcon />}
          >
            {t("Exit")}
          </Button>
        </Box>
      </Box>
    );
  }

  const currentItem = quizList[currentQuestion] ?? {};
  const correctAnswer = currentItem.answer ?? "";

  return (
    <Box
      sx={{
        // p: 6, pt: 2
        px: { xs: 3, sm: 6, md: 6 },
        pt: 2,
        pb: 4,
      }}
    >
      {error ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      ) : quizList.length === 0 ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No quiz found in this category yet.
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Please check back later as we continue to add more.
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="h6" gutterBottom>
            {`${t("Question")} ${currentQuestion + 1} / ${quizList.length}`}
          </Typography>

          {/* Image with loader */}
          <Box
          // sx={{ position: "relative", width: 300, height: 300, mx: "auto", mb: 3 }}
          >
            {imageLoading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255,255,255,0.6)",
                  zIndex: 1,
                }}
              >
                <CircularProgress />
              </Box>
            )}

            <Box
              component="img"
              key={currentQuestion}
              src={currentItem.webpUrl}
              alt="sign"
              onLoad={() => setImageLoading(false)}
              sx={{
                width: "100%",
                height: { xs: 200, sm: 400, md: 400 },
                overflow: "hidden",
                position: "relative",
                objectFit: "contain",
              }}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
              },
              gap: 2,
              mt: 1,
            }}
          >
            {options.map((option, index) => {
              const isSelected = selectedOption?.answer === option.answer;
              const isCorrect = option.answer === correctAnswer;

              let backgroundColor = "white";
              let borderColor = "#ccc";
              if (answerChecked) {
                if (isCorrect) {
                  backgroundColor = "#c8e6c9";
                  borderColor = "green";
                } else if (isSelected && !isCorrect) {
                  backgroundColor = "#ffcdd2";
                  borderColor = "red";
                }
              } else if (isSelected) {
                backgroundColor = "#e0e0e0";
              }

              return (
                <Button
                  key={index}
                  onClick={() => handleOptionClick(option)}
                  disabled={answerChecked}
                  variant="outlined"
                  sx={{
                    textTransform: "none",
                    justifyContent: "space-between",
                    borderColor,
                    borderWidth: 2,
                    borderRadius: 2,
                    height: 40,
                    backgroundColor,
                    color: "black",
                    px: 2,
                  }}
                >
                  <span>
                    {/* {language === "en"
                      ? option.enTitle.charAt(0).toUpperCase() +
                        option.enTitle.slice(1)
                      : option.dzTitle
                      ? option.dzTitle
                      : t(option.enTitle) !== option.enTitle
                      ? t(option.enTitle)
                      : option.enTitle.charAt(0).toUpperCase() +
                        option.enTitle.slice(1)} */}

                    {language === "en"
                      ? option.enTitle: option.dzTitle ? option.dzTitle: t(option.enTitle)}
                  </span>
                  {answerChecked && (
                    <>
                      {isCorrect && <CheckCircleIcon sx={{ color: "green" }} />}
                      {isSelected && !isCorrect && (
                        <CancelIcon sx={{ color: "red" }} />
                      )}
                    </>
                  )}
                </Button>
              );
            })}
          </Box>

          {selectedOption && (
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Button
                variant="contained"
                onClick={answerChecked ? handleNextQuestion : handleCheckAnswer}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: "16px",
                  height: 40,
                  backgroundColor: answerChecked
                    ? selectedOption.answer === correctAnswer
                      ? "#4caf50"
                      : "#f44336"
                    : "#1976d2",
                  "&:hover": {
                    backgroundColor: answerChecked
                      ? selectedOption.answer === correctAnswer
                        ? "#43a047"
                        : "#e53935"
                      : "#1565c0",
                  },
                }}
              >
                {answerChecked
                  ? selectedOption.answer === correctAnswer
                    ? t("Correct! Continue")
                    : t("Nice try! Continue")
                  : t("Check")}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
export default QuizSubScreenOne;

// // backup code 2
// import { useEffect, useState, useRef } from "react";
// import { useParams } from "react-router-dom";
// import {
//   Typography,
//   Button,
//   Box,
//   CircularProgress,
// } from "@mui/material";
// import ReplayIcon from "@mui/icons-material/Replay";
// import ExitToAppIcon from "@mui/icons-material/ExitToApp";
// import CheckCircleIcon from "@mui/icons-material/CheckCircle";
// import CancelIcon from "@mui/icons-material/Cancel";

// import { getQuizByCategory } from "../../../services/database/database_service";
// import LoadingIndicator from "../components/LoadingIndicator";
// import { useLanguage } from "../../../services/localization/LanguageContex";

// function QuizSubScreenOne() {
//   const { t, language } = useLanguage();
//   const { category } = useParams();
//   const [quizList, setQuizList] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   const [currentQuestion, setCurrentQuestion] = useState(0);
//   const [options, setOptions] = useState([]);
//   const [selectedOption, setSelectedOption] = useState(null);
//   const [answerChecked, setAnswerChecked] = useState(false);
//   const [score, setScore] = useState(0);
//   const [showResult, setShowResult] = useState(false);

//   const [audio, setAudio] = useState(null);
//   const hasFetchedRef = useRef(false);

//   useEffect(() => {
//     window.scrollTo(0, 0);
//     if (hasFetchedRef.current) return;
//     hasFetchedRef.current = true;

//     async function fetchQuizByCategory() {
//       try {
//         const data = await getQuizByCategory(category);
//         setQuizList(data.documents);
//       } catch (err) {
//         setError("Could not fetch quiz. Please try again later.");
//       } finally {
//         setLoading(false);
//       }
//     }
//     fetchQuizByCategory();
//   }, [category]);

//   useEffect(() => {
//     if (!loading && quizList.length > 0) {
//       generateOptions();
//     }
//   }, [loading, currentQuestion]);

// // Stop audio on unmount
// useEffect(() => {
//   const stopAudio = () => {
//     if (audio) {
//       audio.pause();
//       audio.currentTime = 0;
//     }
//   };
//   return () => {
//     stopAudio();
//   };
// }, [audio]);

// // Stop audio on tab switch or browser close
// useEffect(() => {
//   const handleVisibilityChange = () => {
//     if (document.hidden && audio) {
//       audio.pause();
//     }
//   };

//   const handleBeforeUnload = () => {
//     if (audio) {
//       audio.pause();
//       audio.currentTime = 0;
//     }
//   };

//   document.addEventListener("visibilitychange", handleVisibilityChange);
//   window.addEventListener("beforeunload", handleBeforeUnload);

//   return () => {
//     document.removeEventListener("visibilitychange", handleVisibilityChange);
//     window.removeEventListener("beforeunload", handleBeforeUnload);
//   };
// }, [audio]);

//   // useEffect(() => {
//   //   return () => {
//   //     if (audio) {
//   //       audio.pause();
//   //       audio.currentTime = 0;
//   //     }
//   //   };
//   // }, [audio]);

//   const generateOptions = () => {
//     if (!quizList.length) return;

//     const correctItem = quizList[currentQuestion];
//     const optionsSet = new Map();
//     optionsSet.set(correctItem.answer, correctItem);

//     while (optionsSet.size < Math.min(4, quizList.length)) {
//       const randIndex = Math.floor(Math.random() * quizList.length);
//       const item = quizList[randIndex];
//       optionsSet.set(item.answer, item); // skip duplicates
//     }

//     const shuffled = Array.from(optionsSet.values()).sort(() => Math.random() - 0.5);
//     setOptions(shuffled);
//   };

//   const playAudio = (url) => {
//     if (audio) {
//       audio.pause();
//       audio.currentTime = 0;
//     }

//     const newAudio = new Audio(url);
//     newAudio.play().catch((err) => {
//       console.error("Playback failed:", err);
//     });

//     setAudio(newAudio);
//   };

//   const handleOptionClick = (option) => {
//     if (answerChecked) return;
//     setSelectedOption(option);

//     if (option?.audioUrl) {
//       playAudio(option.audioUrl);
//     }
//   };

//   const handleCheckAnswer = () => {
//     setAnswerChecked(true);
//     const correct = quizList[currentQuestion].answer;
//     if (selectedOption.answer === correct) {
//       setScore((prev) => prev + 1);
//     }
//   };

//   const handleNextQuestion = () => {
//     if (audio) {
//       audio.pause();
//       audio.currentTime = 0;
//     }
//     if (currentQuestion + 1 < quizList.length) {
//       setCurrentQuestion(currentQuestion + 1);
//       setSelectedOption(null);
//       setAnswerChecked(false);
//     } else {
//       setShowResult(true);
//     }
//   };

//   const restartQuiz = () => {
//     window.location.reload();
//   };

//   if (loading) {
//     return (
//       <Box sx={{ p: 6, pt: 2, textAlign: "center" }}>
//         <LoadingIndicator />
//       </Box>
//     );
//   }

//   if (showResult) {
//     const percentage = Math.round((score / quizList.length) * 100);

//     return (
//       <Box sx={{ p: 4, pt: 6, textAlign: "center" }}>

//         <Typography variant="h5" gutterBottom sx={{ mb: 4 }}>
//           {((score===quizList.length) && (language === "en") )? "Perfect! ":""}
//           {t("Your score")}
//         </Typography>

//         <Box sx={{ position: "relative", display: "inline-flex", mb: 4 }}>
//           <CircularProgress
//             variant="determinate"
//             value={percentage}
//             size={160}
//             thickness={4}
//             sx={{ color: "#4caf50", backgroundColor: "transparent" }}
//           />
//           <Box
//             sx={{
//               top: 0,
//               left: 0,
//               bottom: 0,
//               right: 0,
//               position: "absolute",
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//             }}
//           >
//             <Typography variant="h5" component="div" color="textPrimary" fontWeight="medium">
//               {`${percentage} %`}
//             </Typography>
//           </Box>
//         </Box>

//         <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
//           <Button onClick={restartQuiz} variant="contained" endIcon={<ReplayIcon />}>
//             {t("Try again")}
//           </Button>
//           <Button onClick={() => (window.location.href = "/home/quiz")} variant="contained" endIcon={<ExitToAppIcon />}>
//             {t("Exit")}
//           </Button>
//         </Box>
//       </Box>
//     );
//   }

//   const currentItem = quizList[currentQuestion] ?? {};
//   const correctAnswer = currentItem.answer ?? "";

//   return (
//     <Box sx={{ p: 6, pt: 2 }}>
//       {error ? (
//         <Box sx={{ textAlign: "center", mt: 4 }}>
//           <Typography variant="h6" color="error">
//             {error}
//           </Typography>
//         </Box>
//       ) : quizList.length === 0 ? (
//         <Box sx={{ textAlign: "center", mt: 4 }}>
//           <Typography variant="h6" color="textSecondary" gutterBottom>
//             No quiz found in this category yet.
//           </Typography>
//           <Typography variant="body1" color="textSecondary">
//             Please check back later as we continue to add more.
//           </Typography>
//         </Box>
//       ) : (
//         <>
//           <Typography variant="h6" gutterBottom>
//             {`${t("Question")} ${currentQuestion + 1} / ${quizList.length}`}
//              {/* - {currentItem.answer}-{currentItem.dzTitle} */}
//           </Typography>

//           <Box
//             component="img"
//             key={currentQuestion}
//             src={currentItem.webpUrl}
//             alt="sign"
//             sx={{
//               width: "300px",
//               height: "300px",
//               objectFit: "contain",
//               mb: 3,
//               display: "block",
//               mx: "auto",
//             }}
//           />

//           <Box
//             sx={{
//               display: "grid",
//               gridTemplateColumns: {
//                 xs: "1fr",
//                 sm: "1fr 1fr",
//               },
//               gap: 2,
//               mt: 2,
//             }}
//           >
//             {options.map((option, index) => {
//               const isSelected = selectedOption?.answer === option.answer;
//               const isCorrect = option.answer === correctAnswer;

//               let backgroundColor = "white";
//               let borderColor = "#ccc";
//               if (answerChecked) {
//                 if (isCorrect) {
//                   backgroundColor = "#c8e6c9";
//                   borderColor = "green";
//                 } else if (isSelected && !isCorrect) {
//                   backgroundColor = "#ffcdd2";
//                   borderColor = "red";
//                 }
//               } else if (isSelected) {
//                 backgroundColor = "#e0e0e0";
//               }

//               return (
//                 <Button
//                   key={index}
//                   onClick={() => handleOptionClick(option)}
//                   disabled={answerChecked}
//                   variant="outlined"
//                   sx={{
//                     textTransform: "none",
//                     justifyContent: "space-between",
//                     borderColor,
//                     borderWidth: 2,
//                     borderRadius: 2,
//                     height: 40,
//                     backgroundColor,
//                     color: "black",
//                     px: 2,
//                   }}
//                 >
//                   <span>

//                       {language === "en"
//                         ? option.enTitle.charAt(0).toUpperCase() + option.enTitle.slice(1)
//                         : option.dzTitle
//                           ? option.dzTitle
//                           : (t(option.enTitle) !== option.enTitle
//                               ? t(option.enTitle)
//                               : option.enTitle.charAt(0).toUpperCase() + option.enTitle.slice(1)
//                             )
//                       }
//                   </span>
//                   {answerChecked && (
//                     <>
//                       {isCorrect && <CheckCircleIcon sx={{ color: "green" }} />}
//                       {isSelected && !isCorrect && <CancelIcon sx={{ color: "red" }} />}
//                     </>
//                   )}
//                 </Button>
//               );
//             })}
//           </Box>

//           {selectedOption && (
//             <Box sx={{ textAlign: "center", mt: 4 }}>
//               <Button
//                 variant="contained"
//                 onClick={answerChecked ? handleNextQuestion : handleCheckAnswer}
//                 sx={{
//                   px: 4,
//                   py: 1.5,
//                   fontSize: "16px",
//                   height: 40,
//                   backgroundColor: answerChecked
//                     ? selectedOption.answer === correctAnswer
//                       ? "#4caf50"
//                       : "#f44336"
//                     : "#1976d2",
//                   "&:hover": {
//                     backgroundColor: answerChecked
//                       ? selectedOption.answer === correctAnswer
//                         ? "#43a047"
//                         : "#e53935"
//                       : "#1565c0",
//                   },
//                 }}
//               >
//                 {answerChecked
//                   ? selectedOption.answer === correctAnswer
//                     ? t("Correct! Continue")
//                     : t("Nice try! Continue")
//                   : t("Check")}
//               </Button>
//             </Box>
//           )}
//         </>
//       )}
//     </Box>
//   );
// }
// export default QuizSubScreenOne;

//// backup code 1

// import  { useEffect, useState,useRef } from "react";
// import { useParams } from "react-router-dom";
// import {
//   Typography,
//   Button,
//   Box
// } from "@mui/material";
// import { getQuizByCategory } from "../../../services/database/database_service";
// import LoadingIndicator from "../components/LoadingIndicator";
// import CircularProgress from "@mui/material/CircularProgress";
// import ReplayIcon from '@mui/icons-material/Replay';
// import ExitToAppIcon from '@mui/icons-material/ExitToApp';

// import CheckCircleIcon from "@mui/icons-material/CheckCircle";
// import CancelIcon from "@mui/icons-material/Cancel";
// import { useLanguage } from "../../../services/localization/LanguageContex";

// function QuizSubScreenOne() {
//   const {t ,language} = useLanguage();
//   const { category } = useParams();
//   const [quizList, setQuizList] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   const [currentQuestion, setCurrentQuestion] = useState(0);
//   const [options, setOptions] = useState([]);
//   const [selectedOption, setSelectedOption] = useState(null);
//   const [answerChecked, setAnswerChecked] = useState(false);
//   const [score, setScore] = useState(0);
//   const [showResult, setShowResult] = useState(false);

//   const [audio, setAudio] = useState(null);

//   const hasFetchedRef = useRef(false); // ✅ This will persist across rerenders

//   useEffect(() => {
//     window.scrollTo(0, 0);
//       if (hasFetchedRef.current) return; // ✅ Already fetched? Skip.
//       hasFetchedRef.current = true;

//     async function fetchQuizByCategory() {
//       try {
//         const data = await getQuizByCategory(category);
//         setQuizList(data.documents);
//       } catch (err) {
//         setError("Could not fetch quiz. Please try again later.");
//       } finally {
//         setLoading(false);
//       }
//     }
//     fetchQuizByCategory();
//   }, [category]);

//   console.log(quizList);

//   useEffect(() => {
//     if (!loading && quizList.length > 0) {
//       generateOptions();
//     }
//   }, [loading, currentQuestion]);

//   useEffect(() => {
//     return () => {
//       if (audio) {
//         audio.pause();
//         audio.currentTime = 0;
//       }
//     };
//   }, [audio]);

//   const generateOptions = () => {
//     if (!quizList.length) return;

//     const correctAnswer = quizList[currentQuestion].answer;
//     const totalOptions = Math.min(4, quizList.length);
//     const optionsSet = new Set();
//     optionsSet.add(correctAnswer);

//     while (optionsSet.size < totalOptions) {
//       const randIndex = Math.floor(Math.random() * quizList.length);
//       const option = quizList[randIndex].answer;
//       optionsSet.add(option);
//     }

//     const shuffled = Array.from(optionsSet).sort(() => Math.random() - 0.5);
//     setOptions(shuffled);
//   };

//   const playAudio = (url) => {
//     if (audio) {
//       audio.pause();
//       audio.currentTime = 0;
//     }

//     const newAudio = new Audio(url);

//     // newAudio.onerror = (e) => {
//     //   console.error("Audio playback error:", e);
//     //   alert("Unable to load audio. Please check your internet connection.");
//     // };

//     newAudio.play().catch((err) => {
//       console.error("Playback failed:", err);
//     });

//     setAudio(newAudio);
//   };

//   const handleOptionClick = (option) => {
//       if (answerChecked) return;
//       setSelectedOption(option);

//   // Find the item in quizList that has this answer
//   const matchedItem = quizList.find(item => item.answer === option);
//   if (matchedItem?.audioUrl) {
//     playAudio(matchedItem.audioUrl);
//   }
//   };

//   const handleCheckAnswer = () => {
//     setAnswerChecked(true);
//     const correct = quizList[currentQuestion].answer;
//     // console.log(correct, "correct answer in handleCheckAnswer");
//     if (selectedOption === correct) {
//       setScore(prev => prev + 1);
//     }
//   };

//   const handleNextQuestion = () => {
//     if (audio) {
//       audio.pause();
//       audio.currentTime = 0;
//     }
//     if (currentQuestion + 1 < quizList.length) {
//       setCurrentQuestion(currentQuestion + 1);
//       setSelectedOption(null);
//       setAnswerChecked(false);
//     } else {
//       setShowResult(true);
//     }
//   };

//   const restartQuiz = () => {
//     window.location.reload();
//   };

//   if (loading) {
//     return (
//       <Box sx={{ p: 6, pt: 2, textAlign: "center" }}>
//         <LoadingIndicator />
//       </Box>
//     );
//   }

// if (showResult) {
//   const percentage = Math.round((score / quizList.length) * 100);

//   return (
//     <Box sx={{ p: 4, pt: 6, textAlign: "center" }}>
//       <Typography variant="h5" gutterBottom sx={{ mb: 4 }}>
//         {t("Your score")}
//       </Typography>

//       <Box sx={{ position: "relative", display: "inline-flex", mb: 4 }}>
//         <CircularProgress
//           variant="determinate"
//           value={percentage}
//           size={160}
//           thickness={4}
//           sx={{
//             color: "#4caf50",
//             backgroundColor: "transparent",
//           }}
//         />
//         <Box
//           sx={{
//             top: 0,
//             left: 0,
//             bottom: 0,
//             right: 0,
//             position: "absolute",
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "center",
//           }}
//         >
//           <Typography
//             variant="h5"
//             component="div"
//             color="textPrimary"
//             fontWeight="medium"
//           >
//             {`${percentage} %`}
//           </Typography>
//         </Box>
//       </Box>

//       <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
//         <Button
//           onClick={restartQuiz}
//           variant="contained"
//           endIcon={<ReplayIcon />}
//         >
//           {t("Try again")}
//         </Button>

//         <Button
//           onClick={() => (window.location.href = "/home/quiz")}
//           variant="contained"
//           endIcon={<ExitToAppIcon />}
//         >
//           {t("Exit")}
//         </Button>
//       </Box>
//     </Box>
//   );
// }

//   const currentItem = quizList[currentQuestion] ?? {};
//   const correctAnswer = currentItem.answer ?? "";

//   return (
//     <Box sx={{ p: 6, pt: 2 }}>
//       {error ? (
//         <Box sx={{ textAlign: "center", mt: 4 }}>
//           <Typography variant="h6" color="error">
//             {error}
//           </Typography>
//         </Box>
//       ) : quizList.length === 0 ? (
//         <Box sx={{ textAlign: "center", mt: 4 }}>
//           <Typography variant="h6" color="textSecondary" gutterBottom>
//             No quiz found in this category yet.
//           </Typography>
//           <Typography variant="body1" color="textSecondary">
//             Please check back later as we continue to add more.
//           </Typography>
//         </Box>
//       ) : (
//         <>
//           <Typography variant="h6" gutterBottom>
//             {`${t("Question")}

//             ${currentQuestion + 1} / ${quizList.length}`}

//             {/* -----{currentItem.answer} --- {currentQuestion} */}
//           </Typography>

//           <Box
//             component="img"
//             key={currentQuestion} // Ensure the image updates when currentQuestion changes
//             src={currentItem.webpUrl}
//             alt="sign"
//             sx={{
//               width: "300px",
//               height: "300px",
//               objectFit: "contain",
//               mb: 3,
//               display: "block",
//               mx: "auto",
//             }}
//           />

//           <Box
//             sx={{
//               display: "grid",
//               gridTemplateColumns: {
//                 xs: "1fr",
//                 sm: "1fr 1fr",
//               },
//               gap: 2,
//               mt: 2,
//             }}
//           >
//             {options.map((option, index) => {
//               const isSelected = selectedOption === option;
//               const isCorrect = option === correctAnswer;

//               let backgroundColor = "white";
//               let borderColor = "#ccc";
//               if (answerChecked) {
//                 if (isCorrect) {
//                   backgroundColor = "#c8e6c9"; // green for correct answer
//                   borderColor = "green";
//                 }

//                 if (isSelected && !isCorrect) {
//                   backgroundColor = "#ffcdd2"; // red for wrong selection
//                   borderColor = "red";
//                 }
//               }
//               else if (isSelected) {
//                 backgroundColor = "#e0e0e0";
//               }

//               return (
//                 <Button
//                   key={index}
//                   onClick={() => handleOptionClick(option)}
//                   disabled={answerChecked}
//                   variant="outlined"
//                   sx={{
//                     textTransform: "none",
//                     justifyContent: "space-between",
//                     borderColor,
//                     borderWidth: 2,
//                     borderRadius: 2,
//                     height: 40,
//                     backgroundColor,
//                     color: "black",
//                     px: 2,
//                   }}
//                 >
//                   <span>{t(option)}</span>
//                   {/* <span>
//                     {language === "dz" ? currentItem?.dzTitle || t(option) : t(option)}

//                   </span> */}

//                   {answerChecked && (
//                     <>
//                       {isCorrect && <CheckCircleIcon sx={{ color: "green" }} />}
//                       {isSelected && !isCorrect && <CancelIcon sx={{ color: "red" }} />}
//                     </>
//                   )}
//                 </Button>
//               );

//             })}
//           </Box>

//           {selectedOption && (
//             <Box sx={{ textAlign: "center", mt: 4 }}>
//               <Button
//                 variant="contained"
//                 onClick={answerChecked ? handleNextQuestion : handleCheckAnswer}
//                 sx={{
//                   px: 4,
//                   py: 1.5,
//                   fontSize: "16px",height: 40,
//                   backgroundColor: answerChecked
//                     ? selectedOption === correctAnswer
//                       ? "#4caf50"
//                       : "#f44336"
//                     : "#1976d2",
//                   "&:hover": {
//                     backgroundColor: answerChecked
//                       ? selectedOption === correctAnswer
//                         ? "#43a047"
//                         : "#e53935"
//                       : "#1565c0",
//                   },
//                 }}
//               >
//                 {answerChecked
//                   ? selectedOption === correctAnswer
//                     ? t("Correct! Continue")
//                     : t("Nice try! Continue")
//                   : t("Check")}
//               </Button>
//             </Box>
//           )}
//         </>
//       )}
//     </Box>
//   );
// }
// export default QuizSubScreenOne;

// if (showResult) {
//   return (
//     <Box sx={{ p: 6, pt: 2, textAlign: "center" }}>
//       <Typography variant="h4" gutterBottom>
//         Quiz Complete!
//       </Typography>
//       <Typography variant="h5" gutterBottom>
//         You scored {score} out of {quizList.length}
//       </Typography>
//       <Button
//         onClick={restartQuiz}
//         variant="contained"
//         startIcon={<ArrowForwardIcon />}
//       >
//         Try Again
//       </Button>
//       <br/>
//       <br/>
//        <Button
//         onClick={() => window.location.href = "/home/quizzes"}
//         variant="contained"
//         // startIcon={<ArrowForwardIcon />}
//       >
//         Exit
//       </Button>
//     </Box>
//   );
// }
